import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { compactNaira } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Target } from "lucide-react";
import { toast } from "sonner";
import { C } from "./AccountingLayout";

export default function BudgetActual() {
  const { locationId, period } = useOutletContext();
  const [data, setData] = useState(null);
  const [accs, setAccs] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ account_code: "", period: period, location_id: locationId || "", amount: 0 });

  const load = () => api.get("/reports/budget-variance", { params: { period, ...(locationId ? { location_id: locationId } : {}) } }).then(setData).catch(() => {});
  useEffect(() => { load(); api.get("/accounts").then(setAccs).catch(() => {}); /* eslint-disable-next-line */ }, [period, locationId]);
  useEffect(() => { setDraft((d) => ({ ...d, period, location_id: locationId || "" })); }, [period, locationId]);

  const save = async () => {
    try {
      await api.post("/budgets", { ...draft, amount: Number(draft.amount), location_id: draft.location_id || null });
      toast.success("Budget saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="budget-page">
      <div className="flex items-center justify-between">
        <div className="text-[13px]" style={{ color: C.muted }}>Period: <span className="font-semibold" style={{ color: C.text }}>{period}</span></div>
        <Button onClick={() => setOpen(true)} style={{ background: C.burgundy }} className="text-white" data-testid="new-budget-btn"><Plus size={15} className="mr-1" /> Set Budget</Button>
      </div>
      <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: C.border }}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}>
            <th className="py-3 px-4">Account</th><th className="py-3 px-3 text-right">Budget</th>
            <th className="py-3 px-3 text-right">Actual</th><th className="py-3 px-3 text-right">Variance</th><th className="py-3 px-4 text-right">Variance %</th>
          </tr></thead>
          <tbody>{!data?.rows?.length ? <tr><td colSpan={5} className="py-8 text-center" style={{ color: C.softMuted }}>No budgets set yet — click "Set Budget" to create one.</td></tr> : data.rows.map((r, i) => (
            <tr key={i} className="border-b" style={{ borderColor: C.borderSoft }}>
              <td className="py-2.5 px-4"><span className="font-mono text-[11px] mr-1.5" style={{ color: C.softMuted }}>{r.account_code}</span><span className="font-semibold" style={{ color: C.text }}>{r.account_name}</span></td>
              <td className="py-2.5 px-3 text-right tabular-nums">{compactNaira(r.budget)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums font-brand font-bold" style={{ color: C.text }}>{compactNaira(r.actual)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: r.variance >= 0 ? C.green : C.red }}>{compactNaira(Math.abs(r.variance))}</td>
              <td className="py-2.5 px-4 text-right font-semibold" style={{ color: r.variance_pct >= 0 ? C.green : C.red }}>{r.variance_pct.toFixed(2)}%</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-brand flex items-center gap-2"><Target size={18} style={{ color: C.burgundy }} /> Set Budget</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Account</Label>
              <Select value={draft.account_code} onValueChange={(v) => setDraft({ ...draft, account_code: v })}>
                <SelectTrigger data-testid="bud-account"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{accs.filter((a) => a.active !== false).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Period</Label><Input value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value })} data-testid="bud-period" /></div>
              <div className="space-y-1"><Label>Amount (₦)</Label><Input type="number" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="bud-amount" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="text-white" style={{ background: C.burgundy }} data-testid="bud-save">Save Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
